import moment from 'moment/moment';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import { AnalyticsEngagementSubcategoryScoreOverTime } from '@treams/treams-network';
import { Button, Card, Paragraph, Subheading } from '@treams/treams-ui';

import { LineChartGradient } from '../../metrics/AnalyticsGraphMetric';
import DoughnutMetric from './DoughnutMetric';
import { EngagementDeltaTooltip } from './EngagementDeltaTooltip';

const Container = styled(Card)`
	background: linear-gradient(${(props) => rgba(props.theme.colors.employee, 0.1)}, white 100px);
`;

export default function EngagementMetric(props: {
	data?: AnalyticsEngagementSubcategoryScoreOverTime;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const theme = useTheme();

	const { location } = useHistory();
	const { params } = useRouteMatch<{ metricGroupID: string; metricID: string; tabID?: string }>();

	if (!props.data) {
		return (
			<Container>
				<Flex p={3} alignItems="center">
					<DoughnutMetric.Skeleton padding={0} size={50} fontSize={16} animated={false} />
					<Box ml={3}>{t('dashboard.metrics.engagement.metric.emptyTitle')}</Box>
				</Flex>
			</Container>
		);
	}

	const points = props.data.datapoints.filter(
		(point): point is typeof point & { score: number } => typeof point.score === 'number',
	);

	const score = points[points.length - 1]?.score ?? 0.0;

	const feedbackPath = `/analytics/metric/${params.metricGroupID}/${params.metricID}/feedback${location.search}#${props.data?.subcategory.id}`;

	return (
		<Container>
			<Flex alignItems="center" mb={3}>
				<Box flexGrow={0} flexShrink={0} mr={3}>
					<DoughnutMetric
						padding={0}
						size={50}
						fontSize={16}
						data={[
							{
								value: score,
								color: 'employee',
							},
							{
								value: 10.0 - score,
								color: 'employee',
								hidden: true,
							},
						]}
						digits={1}
						legend={false}
						tooltip={false}
					/>
					{typeof props.data.delta !== 'number' ? (
						<Box width={32} />
					) : (
						<EngagementDeltaTooltip delta={props.data.delta} />
					)}
				</Box>
				<Box flexGrow={1}>
					{points.length > 1 ? (
						<LineChartGradient
							labels={points.map((point) => moment(point.timestamp).format('D MMM'))}
							data={points.map((point) => ({
								x: new Date(point.timestamp).getTime(),
								y: point.score,
							}))}
							height={90}
							tintColor={theme.colors.employee}
							autoBound
							bounds={[0, 11]}
							yFormat={(y: number) => y.toFixed(1)}
						/>
					) : (
						<Paragraph>{t('dashboard.metrics.engagement.metric.emptyTitle')}</Paragraph>
					)}
				</Box>
			</Flex>
			<Box>
				<Subheading py={1}>{localize(props.data.subcategory.title).text}</Subheading>
				<Paragraph>
					{props.data.subcategory.description && props.data.subcategory.description.length > 0 ? (
						localize(props.data.subcategory.description).text
					) : (
						<Paragraph>{t('dashboard.metrics.engagement.metric.emptyDescription')}</Paragraph>
					)}
				</Paragraph>
				<Button
					mt={3}
					link={feedbackPath}
					type={'card'}
					rightIcon={<ChevronRightIcon />}
					title={t('dashboard.metrics.engagement.metric.viewFeedback.title')}
					small
				/>
			</Box>
		</Container>
	);
}
